import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router';
import { useT } from '../../i18n';
import { defaultDeviceName } from '../../lib/device';
import { useSession } from '../../store/session';
import { ApiError, NetworkError } from '../../sync/api';
import controls from '../controls.module.css';
import styles from './Auth.module.css';
import { AuthLayout, Field } from './AuthLayout';

/** Account creation, reached by an invite link or, when the admin opened it, from the sign-in page. */
export function Register() {
  const t = useT();
  const navigate = useNavigate();
  const { token: inviteToken } = useParams<{ token: string }>();
  const status = useSession((s) => s.status);
  const meta = useSession((s) => s.meta);
  const register = useSession((s) => s.register);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [deviceName, setDeviceName] = useState(defaultDeviceName);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (status === 'authed') return <Navigate to="/" replace />;
  const allowed = !!inviteToken || meta?.registrationOpen === true;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await register({ username: username.trim().toLowerCase(), password, deviceName: deviceName.trim() }, inviteToken);
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) setError(t.error(err.code));
      else if (err instanceof NetworkError) setError(t.t('auth.serverOffline'));
      else setError(t.error(undefined));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title={inviteToken ? t.t('auth.inviteHint') : undefined}>
      {allowed ? (
        <form className={styles.form} onSubmit={(e) => void submit(e)}>
          <h2 className="display" style={{ margin: 0, fontSize: 20 }}>
            {t.t('auth.registerTitle')}
          </h2>
          <Field label={t.t('auth.username')}>
            <input className={controls.field} value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} required />
          </Field>
          <Field label={t.t('auth.password')}>
            <input className={controls.field} type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" required />
          </Field>
          <Field label={t.t('auth.device')} hint={t.t('auth.deviceHint')}>
            <input className={controls.field} value={deviceName} onChange={(e) => setDeviceName(e.target.value)} required />
          </Field>
          {error ? <p className={styles.error} role="alert">{error}</p> : null}
          <button type="submit" className={`${controls.btn} ${controls.primary}`} disabled={busy}>
            {busy ? t.t('auth.working') : t.t('auth.create')}
          </button>
        </form>
      ) : (
        <p className={styles.hint}>{t.t('auth.error.REGISTRATION_CLOSED')}</p>
      )}
      <p className={styles.footer}>
        <Link to="/login" className={styles.link}>
          {t.t('auth.haveAccount')}
        </Link>
      </p>
    </AuthLayout>
  );
}
